/*!

=========================================================
* Vision UI Free Chakra - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-chakra
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-chakra/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React, { useEffect } from "react";

// Chakra imports
import {
  Flex,
  Table,
  Tbody,
  Icon,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

// Custom components
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

// Table Components
import TablesProjectRow from "components/Tables/TablesProjectRow";
import TablesTableRow from "components/Tables/TablesTableRow";

// Data
import { tablesProjectData, tablesTableData } from "variables/general";

// Icons
import { AiFillCheckCircle } from "react-icons/ai";
import { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { FETCH_OPERATORS } from "utils/queries";
import TablesValidatorsRow from "components/Tables/TablesValidatorsRow";
import { useParams } from "react-router-dom";
import SkeletonTable from "components/Tables/SkeletonTable";
import { FETCH_OPERATOR_BY_ADDRESS } from "utils/queries";
import Header from "./Header";
import StatsChart from "./StatsChart";
import Attestations from "views/Attestations/Attestations";

const ValidatorDetails = () => {
  const [validatorsData, setValidatorsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { operatorAddress } = useParams();
  const [getValidatorData, { data: signerAddressData }] = useLazyQuery(
    FETCH_OPERATOR_BY_ADDRESS,
    {
      variables: {
        address: operatorAddress,
      },
    }
  );
  useEffect(() => {
    fetchValidatorInfo();
  }, [operatorAddress]);

  useEffect(() => {
    signerAddressData?.operators &&
      setValidatorsData(signerAddressData?.operators);
  }, [signerAddressData]);

  const fetchValidatorInfo = async () => {
    setIsLoading(true);
    await getValidatorData();
    setIsLoading(false);
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Header />
      {validatorsData.length > 0 && (
        <StatsChart operatorId={validatorsData[0].operatorId} />
      )}
      <Card
        overflowX={{ sm: "scroll", xl: "hidden" }}
        pb="0px"
        className="scroll-content"
      >
        <CardHeader p="6px 0px 22px 0px">
          <Text fontSize="lg" color="#fff" fontWeight="bold">
            Operator Details
          </Text>
        </CardHeader>
        <CardBody>
          <Table variant="simple" color="#fff">
            <Thead>
              <Tr my=".8rem" ps="0px" color="gray.400">
                <Th
                  ps="0px"
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  ID
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Operator
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Status
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Stake
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  APY
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Active
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Finalized Block
                </Th>

                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Attested Dynasty
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Attested Epoch
                </Th>

                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Epoch Churned Out
                </Th>
                <Th borderBottomColor="#56577A"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading && validatorsData.length === 0 ? (
                <SkeletonTable rowCount={10} columnCount={11} />
              ) : validatorsData.length === 0 ? (
                <TablesValidatorsRow
                  key={0}
                  address={null}
                  operatorId={null}
                  stake={null}
                  apy={null}
                  lastAttestedDynasty={null}
                  lastAttestedEpoch={null}
                  lastFinalizedBlock={null}
                  status={null}
                  lastEpochChurnedOut={null}
                  lastItem={true}
                  lastActiveTimestamp={null}
                />
              ) : (
                validatorsData.map((row, index, arr) => {
                  return (
                    <TablesValidatorsRow
                      key={row.address}
                      address={row.address}
                      operatorId={row.operatorId}
                      stake={row.stake / 1e18}
                      apy={row.apy}
                      lastActiveTimestamp={row.lastActiveTimestamp}
                      lastAttestedDynasty={row.lastAttestedDynasty}
                      lastAttestedEpoch={row.lastAttestedEpoch}
                      lastFinalizedBlock={row.lastFinalizedBlock}
                      status={row.status}
                      lastEpochChurnedOut={row.lastEpochChurnedOut}
                      lastItem={index === arr.length - 1 ? true : false}
                    />
                  );
                })
              )}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
      <Attestations />
    </Flex>
  );
};

export default ValidatorDetails;
